import React from 'react'
import EyeIcon from 'assets/icons/eye-2-line-icon'
import ConfirmIcon from 'assets/icons/confirm-icon'
import BlockIcon from 'assets/icons/block-icon'
import DelistIcon from 'assets/icons/delist-icon'
import ViewIcon from 'assets/icons/view-icon'
import SupersedeIcon from 'assets/icons/supersede-icon'
import RemoveIcon from 'assets/icons/remove-icon16px'
import {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_DOES_NOT_QUALIFY,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_NOMINATED_ID,
  SITE_STATUS_NOT_ASSESSED_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID,
  SITE_STATUS_SUPERSEDED_ID,
} from '../siteStatuses'
import {
  getSupersedeOrUndoMenuTitle,
  removeSupersedeIfKbaEditOnly,
  removeSupersedeIfKbaStatusTooLow,
  withSupersedeModalIntent,
} from '../../utils/supersedeMenuHelpers'
import { BIRDLIFE_SECRETARIAT_ROLE_ID } from '../roles'
import {
  canBirdLifeSecretariatConfirmSite,
  canBirdLifeSecretariatDelistOrSupersede,
  canBirdLifeSecretariatSupersedeOrUndoSupersed,
} from 'utils/siteIbaAccess'
import { getEditOnlyContext } from '../../utils/editOnlyContext'
import {
  createNavigateReviewDetails,
  createNavigateViewSiteWithAllHistories,
} from '../../utils/navigationHandlers'

function isBirdLifeSecretariatProfile(profile) {
  return profile?.RoleId === BIRDLIFE_SECRETARIAT_ROLE_ID
}

function removeDelistAssessmentByRules(site, actionList) {
  if (site.historyId && !site.delist) {
    const delistIndex = actionList.findIndex((item) => item.title === 'delistAssessment')
    if (delistIndex !== -1) {
      actionList.splice(delistIndex, 1)
    }
  }
  if (site.delist == undefined) {
    const delistIndex = actionList.findIndex((item) => item.title === 'delistAssessment')
    if (delistIndex !== -1) {
      actionList.splice(delistIndex, 1)
    }
  }
}

function buildNominatedOrSubmittedSecretariatItems(ctx) {
  const {
    site,
    history,
    setSiteToPublish,
    setSiteToDelist,
    approveEditOnlyCallBack,
    profile,
  } = ctx
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  const isBirdLifeSecretariat = isBirdLifeSecretariatProfile(profile)

  const actionList = [
    {
      icon: <EyeIcon />,
      title: editOnlySiteStatus ? 'reviewEditOnlySite' : 'review',
      onClick: createNavigateReviewDetails(site, history, editOnlySite, editOnlySiteStatus),
    },
  ]

  if (!isBirdLifeSecretariat || canBirdLifeSecretariatConfirmSite(site, profile)) {
    actionList.push({
      icon: <ConfirmIcon />,
      title: editOnlySiteStatus ? 'approveEditOnlySite' : 'confirmAssessmentBtn',
      onClick: () => {
        if (editOnlySiteStatus) {
          approveEditOnlyCallBack({ ...site, isApproved: true })
          return
        }
        setSiteToPublish({ ...site, isApproved: true })
      },
    })
  }

  actionList.push({
    icon: <BlockIcon />,
    title: editOnlySiteStatus ? 'returnToEditOnly' : 'rejectAssessmentBtn',
    onClick: () => {
      if (editOnlySiteStatus) {
        approveEditOnlyCallBack({ ...site, isApproved: false })
        return
      }
      setSiteToPublish({ ...site, isApproved: false })
    },
  })

  if (!isBirdLifeSecretariat || canBirdLifeSecretariatDelistOrSupersede(site, profile)) {
    actionList.push({
      icon: <DelistIcon />,
      title: 'delistAssessment',
      onClick: () => {
        setSiteToDelist(site)
      },
    })
  }

  removeDelistAssessmentByRules(site, actionList)
  return actionList
}

function buildDelistedSecretariatItems(ctx) {
  const { site, history, changeStatusCallback, profile } = ctx
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  const isBirdLifeSecretariat = isBirdLifeSecretariatProfile(profile)

  const delistedActions = [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      onClick: createNavigateViewSiteWithAllHistories(site, history, editOnlySite, editOnlySiteStatus),
    },
  ]

  if (!isBirdLifeSecretariat || canBirdLifeSecretariatSupersedeOrUndoSupersed(site, profile)) {
    delistedActions.push({
      icon: <SupersedeIcon />,
      title: getSupersedeOrUndoMenuTitle(site),
      onClick: () => {
        changeStatusCallback(withSupersedeModalIntent(site, { isApproved: true }))
      },
    })
  }

  return delistedActions
}

function buildNonConfirmedNonDraftSecretariatView(ctx) {
  const { site, history } = ctx
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  return [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      onClick: createNavigateViewSiteWithAllHistories(site, history, editOnlySite, editOnlySiteStatus),
    },
  ]
}

/**
 * In-flight assessment statuses (e.g. proposed, RFP review): secretariat table shows view-only.
 * Does not qualify / not assessed are final KBA outcomes (ND in superseding permutations): use default menu so KBA Sec can supersede per business rules; BirdLife remains gated in buildDefaultSecretariatDropdown.
 */
function shouldUseNonConfirmedViewOnly(site) {
  if (!site.SiteStatus) {
    return false
  }
  const id = site.SiteStatus.id
  if (id === SITE_STATUS_DOES_NOT_QUALIFY || id === SITE_STATUS_NOT_ASSESSED_ID) {
    return false
  }
  return (
    id !== SITE_STATUS_CONFIRMED_ID &&
    id !== SITE_STATUS_SUPERSEDED_ID &&
    id !== SITE_STATUS_DRAFT_ID &&
    id !== SITE_STATUS_EDIT_ONLY_ID
  )
}

function buildDefaultSecretariatDropdown(ctx) {
  const { site, history, changeStatusCallback, setSiteForDeleting, profile } = ctx
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  const isBirdLifeSecretariat = isBirdLifeSecretariatProfile(profile)

  const secretariatDropdownItems = [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      onClick: createNavigateViewSiteWithAllHistories(site, history, editOnlySite, editOnlySiteStatus),
    },
  ]

  if (!isBirdLifeSecretariat || canBirdLifeSecretariatSupersedeOrUndoSupersed(site, profile)) {
    secretariatDropdownItems.push({
      icon: <SupersedeIcon />,
      title: getSupersedeOrUndoMenuTitle(site),
      onClick: () => {
        changeStatusCallback(withSupersedeModalIntent(site))
      },
    })
  }

  removeSupersedeIfKbaStatusTooLow(secretariatDropdownItems, site)
  removeSupersedeIfKbaEditOnly(secretariatDropdownItems, site)

  if (site.SiteStatus?.id && site.SiteStatus.id === SITE_STATUS_DRAFT_ID && site.historyId) {
    secretariatDropdownItems.push({
      icon: <RemoveIcon />,
      title: 'Remove',
      onClick: () => {
        setSiteForDeleting(site)
      },
    })
  }

  return secretariatDropdownItems
}

export function buildBirdlifeSecretariatAndKbaSecretariatMenuItems(ctx) {
  const { site } = ctx

  if (
    site.SiteStatus?.id === SITE_STATUS_NOMINATED_ID ||
    site.SiteStatus?.id === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  ) {
    return buildNominatedOrSubmittedSecretariatItems(ctx)
  }
  if (site.SiteStatus?.id === SITE_STATUS_DELISTED_ID) {
    return buildDelistedSecretariatItems(ctx)
  }
  if (shouldUseNonConfirmedViewOnly(site)) {
    return buildNonConfirmedNonDraftSecretariatView(ctx)
  }
  return buildDefaultSecretariatDropdown(ctx)
}

export function parseSecretariatMenuArgs(args) {
  const [
    site,
    history,
    changeStatusCallback,
    setSiteToPublish,
    setSiteToDelist,
    ,
    setSiteForDeleting,
    ,
    ,
    approveEditOnlyCallBack,
    profile,
  ] = args

  return {
    site,
    history,
    changeStatusCallback,
    setSiteToPublish,
    setSiteToDelist,
    setSiteForDeleting,
    approveEditOnlyCallBack,
    profile,
  }
}

export function getBirdlifeSecretariatAndKbaSecretariatActionMenuItems(...args) {
  return buildBirdlifeSecretariatAndKbaSecretariatMenuItems(parseSecretariatMenuArgs(args))
}
